import React, { useState, useEffect, useCallback } from 'react';
import { Button, Loader, Dropdown, Input } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../lib/auth/axiosApi';
import './CardGame.css';

// Rarity is a 1-10 level, 10 rarest. Bands drive the styling: 1-3 low, 4-6 mid, 7-9 high, 10 top.
const RARE_FLOOR = 7;
const rarityBand = (lvl) => (lvl >= 10 ? 'top' : lvl >= RARE_FLOOR ? 'high' : lvl >= 4 ? 'mid' : 'low');

function GameCardView({ card, selectable, selected, onClick }) {
  return (
    <div
      className={`gamecard rarity-${rarityBand(card.rarity)} ${selectable ? 'selectable' : ''} ${selected ? 'selected' : ''}`}
      onClick={onClick}
    >
      {card.requires_together && <span className="card-tag" title="All players must be together">🤝</span>}
      <div className="card-name">{card.name}</div>
      <div className="card-desc">{card.description}</div>
      <div className="card-rarity">Lv {card.rarity}</div>
    </div>
  );
}

function CardGamePage() {
  const [tab, setTab] = useState('packs');
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    hand: [], unopened_packs: [], players: [], odds: {}, hand_cap: 10, pity_threshold: 10,
  });
  const [ledger, setLedger] = useState([]);
  const [trades, setTrades] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [plays, setPlays] = useState([]);
  const [notifs, setNotifs] = useState(null); // null = panel closed
  const me = state.me;

  // Pack reveal state
  const [reveal, setReveal] = useState(null); // { cards, pity_applied, flipped: [bool] }

  // Play modal state
  const [playModal, setPlayModal] = useState(null); // { card, target, note }

  // Thread state
  const [expandedPlay, setExpandedPlay] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentFile, setCommentFile] = useState(null);

  // Trade builder state
  const [tradeOpen, setTradeOpen] = useState(false);
  const [counterOf, setCounterOf] = useState(null); // offer being countered
  const [tradeRecipient, setTradeRecipient] = useState('');
  const [giveIds, setGiveIds] = useState([]);
  const [receiveIds, setReceiveIds] = useState([]);
  const [tradeMessage, setTradeMessage] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [stateRes, ledgerRes, tradesRes, catalogRes, playsRes] = await Promise.all([
        axiosInstance.get('cardgame/state/'),
        axiosInstance.get('cardgame/ledger/'),
        axiosInstance.get('cardgame-trades/'),
        axiosInstance.get('cardgame-catalog/'),
        axiosInstance.get('cardgame-plays/'),
      ]);
      const asList = (d) => (Array.isArray(d) ? d : d?.results || []);
      setState(stateRes.data);
      setLedger(asList(ledgerRes.data));
      setTrades(asList(tradesRes.data));
      setCatalog(asList(catalogRes.data));
      setPlays(asList(playsRes.data));
    } catch (err) {
      toast.error('Failed to load the card game.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---------- packs ----------

  const openPack = async (packId) => {
    try {
      const res = await axiosInstance.post('cardgame/open_pack/', { pack_id: packId });
      setReveal({
        cards: res.data.cards,
        pity_applied: res.data.pity_applied,
        flipped: res.data.cards.map(() => false),
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not open pack.');
    }
  };

  const flipCard = (idx) => {
    setReveal(r => ({
      ...r,
      flipped: r.flipped.map((f, i) => (i === idx ? true : f)),
    }));
  };

  const closeReveal = () => {
    setReveal(null);
    fetchAll();
  };

  // ---------- play ----------

  const submitPlay = async () => {
    const { card, target, note } = playModal;
    try {
      await axiosInstance.post('cardgame/play/', {
        owned_card_id: card.id,
        target: target || null,
        note,
      });
      toast.success(`${card.name} played. It's real now — they've been notified.`);
      setPlayModal(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not play card.');
    }
  };

  // ---------- threads ----------

  const submitComment = async (playId) => {
    if (!commentText && !commentFile) {
      toast.error('Say something or attach proof.');
      return;
    }
    try {
      const form = new FormData();
      form.append('text', commentText);
      if (commentFile) form.append('attachment', commentFile);
      await axiosInstance.post(`cardgame-plays/${playId}/comment/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCommentText('');
      setCommentFile(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not post.');
    }
  };

  const verifyPlay = async (playId) => {
    try {
      await axiosInstance.post(`cardgame-plays/${playId}/verify/`);
      toast.success('Verified.');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not verify.');
    }
  };

  const openNotifs = async () => {
    try {
      const res = await axiosInstance.get('cardgame/notifications/');
      setNotifs(Array.isArray(res.data) ? res.data : res.data?.results || []);
      setState(s => ({ ...s, unread_notifications: 0 }));
    } catch (err) {
      toast.error('Could not load notifications.');
    }
  };

  // ---------- trades ----------

  const startTrade = () => {
    setCounterOf(null);
    setGiveIds([]);
    setReceiveIds([]);
    setTradeMessage('');
    setTradeRecipient('');
    setTradeOpen(true);
  };

  const startCounter = (offer) => {
    setCounterOf(offer);
    setGiveIds([]);
    setReceiveIds([]);
    setTradeMessage('');
    setTradeRecipient(offer.proposer);
    setTradeOpen(true);
  };

  const toggleId = (list, setList, id) => {
    setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };

  const submitTrade = async () => {
    if (!counterOf && !tradeRecipient) {
      toast.error('Pick who you are trading with.');
      return;
    }
    if (giveIds.length === 0 && receiveIds.length === 0) {
      toast.error('A trade needs at least one card.');
      return;
    }
    try {
      const payload = { give_ids: giveIds, receive_ids: receiveIds, message: tradeMessage };
      if (counterOf) {
        await axiosInstance.post(`cardgame-trades/${counterOf.id}/counter/`, payload);
        toast.success('Counter-offer sent.');
      } else {
        await axiosInstance.post('cardgame-trades/', { ...payload, recipient: tradeRecipient });
        toast.success('Trade offer sent.');
      }
      setTradeOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Trade failed.');
    }
  };

  const respondToTrade = async (offer, verb) => {
    try {
      await axiosInstance.post(`cardgame-trades/${offer.id}/${verb}/`);
      toast.success(`Trade ${verb}${verb.endsWith('e') ? 'd' : 'ed'}.`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || `Could not ${verb} trade.`);
    }
  };

  if (loading) return <Loader active inline="centered" style={{ marginTop: 60 }} />;

  const pendingForMe = trades.filter(t => t.status === 'pending' && t.recipient_username === me).length;
  const playerOptions = state.players
    .filter(p => p.username !== me)
    .map(p => ({ key: p.id, value: p.id, text: p.username }));

  return (
    <div className="cardgame-page">
      {reveal && (
        <div className="reveal-overlay">
          {reveal.pity_applied && <div className="pity-banner">✨ PITY ACTIVATED — GUARANTEED RARE ✨</div>}
          <div className="reveal-cards">
            {reveal.cards.map((card, idx) => (
              <div
                key={card.id}
                className={`flip-card rarity-${rarityBand(card.rarity)} ${reveal.flipped[idx] ? 'flipped' : ''}`}
                onClick={() => flipCard(idx)}
              >
                <div className="flip-inner">
                  <div className="flip-face flip-back">🂠</div>
                  <div className="flip-face flip-front">
                    <GameCardView card={card} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {reveal.flipped.every(Boolean) ? (
            <Button color="yellow" size="large" onClick={closeReveal}>Take Cards</Button>
          ) : (
            <div className="reveal-hint">TAP A CARD TO REVEAL</div>
          )}
        </div>
      )}

      <div className="cardgame-tabs">
        <button className={`cardgame-tab ${tab === 'packs' ? 'active' : ''}`} onClick={() => setTab('packs')}>
          Packs{state.unopened_packs.length > 0 && <span className="badge">{state.unopened_packs.length}</span>}
        </button>
        <button className={`cardgame-tab ${tab === 'hand' ? 'active' : ''}`} onClick={() => setTab('hand')}>
          My Hand ({state.hand.length}/{state.hand_cap})
        </button>
        <button className={`cardgame-tab ${tab === 'trades' ? 'active' : ''}`} onClick={() => setTab('trades')}>
          Trades{pendingForMe > 0 && <span className="badge">{pendingForMe}</span>}
        </button>
        <button className={`cardgame-tab ${tab === 'ledger' ? 'active' : ''}`} onClick={() => setTab('ledger')}>
          Ledger
        </button>
        <button className={`cardgame-tab ${tab === 'rules' ? 'active' : ''}`} onClick={() => setTab('rules')}>
          Rules
        </button>
        <button className="cardgame-tab" onClick={openNotifs}>
          🔔{state.unread_notifications > 0 && <span className="badge">{state.unread_notifications}</span>}
        </button>
      </div>

      {tab === 'packs' && (
        <div>
          {state.unopened_packs.length === 0 ? (
            <p>No unopened packs. New packs drop every Sunday. 🕐</p>
          ) : (
            <div className="pack-shelf">
              {state.unopened_packs.map(pack => (
                <div key={pack.id} className="pack" onClick={() => openPack(pack.id)}>
                  <div className="pack-logo">🎴</div>
                  <div>{pack.num_cards} CARDS</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>TAP TO RIP</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {playModal && (
        <div className="reveal-overlay" style={{ justifyContent: 'center' }}>
          <div className="rules-box" style={{ maxWidth: 420 }}>
            <h3>Play {playModal.card.name}?</h3>
            <p>{playModal.card.description}</p>
            {playModal.card.target_mode === 'everyone' ? (
              <p><b>Targets: everyone.</b> The whole group gets hit.</p>
            ) : (
              <>
                <p><b>Who does it land on?</b></p>
                <Dropdown
                  placeholder="Pick your victim..." selection fluid options={playerOptions}
                  value={playModal.target}
                  onChange={(e, { value }) => setPlayModal(m => ({ ...m, target: value }))}
                />
              </>
            )}
            <Input
              fluid placeholder="Note (optional) — instructions, trash talk"
              value={playModal.note}
              onChange={e => setPlayModal(m => ({ ...m, note: e.target.value }))}
              style={{ margin: '10px 0' }}
            />
            <p style={{ fontSize: 12, color: '#777' }}>
              This is public, notifies everyone, opens a proof thread, and the card is gone forever.
            </p>
            <Button
              color="red"
              disabled={playModal.card.target_mode !== 'everyone' && !playModal.target}
              onClick={submitPlay}
            >
              PLAY IT
            </Button>
            <Button onClick={() => setPlayModal(null)}>Never mind</Button>
          </div>
        </div>
      )}

      {notifs !== null && (
        <div className="reveal-overlay" style={{ justifyContent: 'center' }}>
          <div className="rules-box" style={{ maxWidth: 480, maxHeight: '70vh', overflowY: 'auto' }}>
            <h3>🔔 Notifications</h3>
            {notifs.length === 0 && <p>Nothing yet. Play more cards.</p>}
            {notifs.map(n => (
              <div key={n.id} className={`ledger-event event-${n.notification_type === 'trade' ? 'trade' : 'play'}`} style={{ marginBottom: 6 }}>
                {n.message}
                <span className="event-time">{new Date(n.created_at).toLocaleString()}</span>
              </div>
            ))}
            <Button onClick={() => setNotifs(null)} style={{ marginTop: 10 }}>Close</Button>
          </div>
        </div>
      )}

      {tab === 'hand' && (
        <div>
          {state.hand.length === 0 && <p>Your hand is empty. Open a pack or beg for a trade.</p>}
          <div className="cardgame-hand">
            {state.hand.map(card => (
              <div key={card.id}>
                <GameCardView card={card} />
                <Button
                  fluid size="tiny" color="red" style={{ marginTop: 6 }}
                  onClick={() => setPlayModal({ card, target: '', note: '' })}
                >
                  PLAY IT
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'trades' && (
        <div>
          {!tradeOpen && <Button color="green" onClick={startTrade}>Propose Trade</Button>}

          {tradeOpen && (
            <div className="trade-offer">
              <h4>{counterOf ? `Counter-offer to ${counterOf.proposer_username}` : 'New Trade Offer'}</h4>
              {!counterOf && (
                <Dropdown
                  placeholder="Trade with..." selection options={playerOptions}
                  value={tradeRecipient} onChange={(e, { value }) => setTradeRecipient(value)}
                />
              )}
              <h5>Your cards to give ({giveIds.length}):</h5>
              <div className="cardgame-hand">
                {state.hand.map(card => (
                  <GameCardView
                    key={card.id} card={card} selectable
                    selected={giveIds.includes(card.id)}
                    onClick={() => toggleId(giveIds, setGiveIds, card.id)}
                  />
                ))}
              </div>
              {counterOf && (
                <>
                  <h5>Their offered cards you want ({receiveIds.length}):</h5>
                  <div className="trade-items">
                    {counterOf.items.filter(i => i.direction === 'give').map(item => (
                      <span
                        key={item.id}
                        className={`trade-chip rarity-${rarityBand(item.rarity)}`}
                        style={{ cursor: 'pointer', outline: receiveIds.includes(item.owned_card) ? '3px solid #27ae60' : 'none' }}
                        onClick={() => toggleId(receiveIds, setReceiveIds, item.owned_card)}
                      >
                        {item.name}
                      </span>
                    ))}
                  </div>
                </>
              )}
              <Input
                fluid placeholder='Message — e.g. "your Heist for these two"'
                value={tradeMessage} onChange={e => setTradeMessage(e.target.value)}
                style={{ margin: '10px 0' }}
              />
              <Button color="green" onClick={submitTrade}>Send Offer</Button>
              <Button onClick={() => setTradeOpen(false)}>Never mind</Button>
              <p style={{ marginTop: 8, fontSize: 12, color: '#777' }}>
                Hands are private — describe what you want in the message, or they can counter with their own cards attached.
              </p>
            </div>
          )}

          {trades.map(offer => {
            const oneSided = !(offer.items.some(i => i.direction === 'give') && offer.items.some(i => i.direction === 'receive'));
            return (
            <div key={offer.id} className="trade-offer">
              <span className={`trade-status status-${offer.status}`}>{offer.status}</span>
              {' '}<b>{offer.proposer_username}</b> → <b>{offer.recipient_username}</b>
              {offer.message && <div style={{ fontStyle: 'italic', margin: '4px 0' }}>"{offer.message}"</div>}
              <div className="trade-items">
                {offer.items.map(item => (
                  <span key={item.id} className={`trade-chip rarity-${rarityBand(item.rarity)}`}>
                    {item.direction === 'give' ? '⬆' : '⬇'} {item.name}
                  </span>
                ))}
              </div>
              {offer.status === 'pending' && offer.recipient_username === me && (
                <div>
                  {oneSided ? (
                    <span style={{ fontSize: 12, color: '#b8860b', display: 'block', marginBottom: 6 }}>
                      ⚠️ One-sided offer — Counter with what you're giving so both of you confirm the swap.
                    </span>
                  ) : (
                    <Button size="tiny" color="green" onClick={() => respondToTrade(offer, 'accept')}>Accept</Button>
                  )}
                  <Button size="tiny" color="red" onClick={() => respondToTrade(offer, 'decline')}>Decline</Button>
                  <Button size="tiny" color="blue" onClick={() => startCounter(offer)}>Counter</Button>
                </div>
              )}
              {offer.status === 'pending' && offer.proposer_username === me && (
                <Button size="tiny" onClick={() => respondToTrade(offer, 'cancel')}>Cancel</Button>
              )}
            </div>
          );
          })}
        </div>
      )}

      {tab === 'ledger' && (
        <div className="ledger-feed">
          {ledger.length === 0 && <p>Nothing has happened yet. Boring group.</p>}
          {ledger.map(ev => {
            const thread = ev.play ? plays.find(p => p.id === ev.play) : null;
            return (
              <div key={ev.id} className={`ledger-event event-${ev.event_type}`}>
                {ev.event_type === 'pull' && (
                  <span>
                    📦 <b>{ev.username}</b> pulled{' '}
                    <span className={ev.card_rarity >= RARE_FLOOR ? 'rare-text' : ''}>
                      {ev.card_name} (Lv {ev.card_rarity})
                    </span>
                  </span>
                )}
                {ev.event_type === 'play' && (
                  <span>
                    🔥 <b>{ev.username}</b> played <b>{ev.card_name}</b>
                    {thread?.target_username && <span> on <b>{thread.target_username}</b></span>}
                    {ev.note && <i> — "{ev.note}"</i>}
                  </span>
                )}
                {ev.event_type === 'trade' && (
                  <span>
                    🤝 <b>{ev.username}</b> and <b>{ev.other_username}</b> completed a trade. Contents secret.
                  </span>
                )}
                <span className="event-time">{new Date(ev.created_at).toLocaleString()}</span>

                {thread && (
                  <div style={{ marginTop: 6 }}>
                    <Button
                      size="mini" basic
                      onClick={() => setExpandedPlay(expandedPlay === thread.id ? null : thread.id)}
                    >
                      💬 {thread.comments.length} · ✅ {thread.verified_by.length}
                    </Button>

                    {expandedPlay === thread.id && (
                      <div style={{ marginTop: 8, paddingLeft: 10, borderLeft: '3px solid #ddd' }}>
                        {thread.comments.map(c => (
                          <div key={c.id} style={{ marginBottom: 8 }}>
                            <b>{c.username}</b>{c.text && <span>: {c.text}</span>}
                            {c.attachment_url && (
                              /\.(jpe?g|png|gif|webp|heic)$/i.test(c.attachment_url) ? (
                                <div><img src={c.attachment_url} alt="proof" style={{ maxWidth: 280, borderRadius: 8, marginTop: 4 }} /></div>
                              ) : /\.(mp3|m4a|wav|ogg)$/i.test(c.attachment_url) ? (
                                <div><audio controls src={c.attachment_url} style={{ marginTop: 4 }} /></div>
                              ) : /\.(mov|mp4|webm)$/i.test(c.attachment_url) ? (
                                <div><video controls src={c.attachment_url} style={{ maxWidth: 280, borderRadius: 8, marginTop: 4 }} /></div>
                              ) : (
                                <div><a href={c.attachment_url} target="_blank" rel="noreferrer">📎 attachment</a></div>
                              )
                            )}
                            <span className="event-time">{new Date(c.created_at).toLocaleString()}</span>
                          </div>
                        ))}
                        {thread.verified_by.length > 0 && (
                          <div style={{ fontSize: 12, color: '#27ae60', marginBottom: 6 }}>
                            ✅ verified by {thread.verified_by.join(', ')}
                          </div>
                        )}
                        <Input
                          fluid size="small" placeholder="Comment..."
                          value={commentText} onChange={e => setCommentText(e.target.value)}
                          style={{ marginBottom: 6 }}
                        />
                        <input
                          type="file" accept="image/*,video/*,audio/*"
                          onChange={e => setCommentFile(e.target.files[0] || null)}
                          style={{ marginBottom: 6, fontSize: 12 }}
                        />
                        <div>
                          <Button size="mini" color="blue" onClick={() => submitComment(thread.id)}>
                            Post {commentFile ? 'proof' : 'comment'}
                          </Button>
                          {!thread.verified_by.includes(me) && (
                            <Button size="mini" color="green" onClick={() => verifyPlay(thread.id)}>
                              ✅ It happened
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'rules' && (
        <div className="rules-box">
          <h3>🎴 What is this</h3>
          <p>
            A deck of cards with real-world effects, between the three of us. Every card in your
            hand can be played once, at any time. Once played, the target must comply (default
            window: 7 days) and the card is gone forever.
          </p>

          <h3>📦 Sunday Packs</h3>
          <p>
            Every Sunday each player gets a pack of 2 cards. Supply is unlimited — rarity is pure
            probability, ratified by unanimous vote and displayed right here:
          </p>
          <div className="odds-table">
            {Object.entries(state.odds).sort((a, b) => Number(a[0]) - Number(b[0])).map(([level, odds]) => (
              <div key={level} className={`odds-pill rarity-${rarityBand(Number(level))}`}>
                Lv {level}<br />{+(odds * 100).toFixed(1)}%
              </div>
            ))}
          </div>
          <p>
            <b>Pity rule:</b> {state.pity_threshold} straight packs without a Lv {RARE_FLOOR}+ =
            guaranteed Lv {RARE_FLOOR}+ in your next pack. <b>Hand cap:</b> {state.hand_cap} cards —
            at cap you must play or trade before opening another pack. Adding new cards to the
            catalog never changes these odds — the level is rolled first, then a card is picked
            evenly within that level (an empty level falls to the next one down).
          </p>

          <h3>👁 What's public, what's secret</h3>
          <p>
            The ledger shows every pull and every play, forever. Current hands are <b>private</b> —
            if you want to know what someone's holding, do the math from the ledger. Trades show{' '}
            <i>that</i> they happened, never what moved.
          </p>

          <h3>🤝 Trading</h3>
          <p>
            Any shape: 1-for-1, 2-for-1, whatever — value is what two players agree on. Offers can
            be accepted, declined, or countered. Trades are final once accepted. No crying.
          </p>

          <h3>🚫 Hard limits (no card overrides these)</h3>
          <p>
            Nothing done <i>to</i> a non-player. Nothing that touches jobs, relationships, or family
            in a real way. Risk-taking cards only bind willing participants. Anyone can invoke{' '}
            <b>"seriously, no"</b> once per card played on them per year — the card bounces back to
            the player's hand. Can't or won't do a card? Buy out for <b>$50 cash</b> to the player.
          </p>

          <h3>🃏 The Catalog ({catalog.length} cards)</h3>
          <div className="cardgame-hand">
            {catalog.map(card => (
              <GameCardView key={card.id} card={card} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CardGamePage;
